import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFViewer,
} from "@react-pdf/renderer";
import { getChangePercentageComparedToPrevYear } from "../utils/summaryUtils";
import { useTranslation } from "react-i18next";
import { ReportingCycleEnum } from "../api/models/reportingCycle.models";
import { isInteger } from "../utils/rowUtils";
import { Report } from "../api/models/report.models";
import { Company, Organization } from "../api/models/companies.models";
import { EcomodClass } from "../api/models/ecomodClass.models";
import { SupClass } from "../api/models/supClass.models";
import { Price } from "../api/models/prices.models";

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#E4E4E4",
    padding: 20,
  },
  header: {
    fontSize: 16,
    marginBottom: 10,
  },
  section: {
    marginTop: 10,
    marginBottom: 10,
  },
  sectionHeader: {
    fontSize: 12,
    marginBottom: 15,
    fontWeight: "bold",
  },
  sectionContent: {
    marginLeft: 10,
  },
  sectionRow: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 3,
    fontSize: 6,
  },
  headerSectionRow: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 3,
    fontSize: 8,
    fontWeight: "bold",
  },
  nameColumn: {
    flex: 2.5,
    whiteSpace: "nowrap",
    maxWidth: "35%",
  },
  numberColumn: {
    flex: 1,
    textAlign: "right",
    whiteSpace: "nowrap",
  },
});

type ReportPdfProps = {
  reportData: Report;
  prevYearReport?: Report | null;
  ecomodClasses: EcomodClass[];
  supClasses: SupClass[];
  pricesData: Price[];
  reportCompany?: Company | null;
  reportOrganization?: Organization | null;
  consumerData: any[];
  businessData: any[];
  supData: any[];
  prevYearConsumerData?: any[];
  prevYearBusinessData?: any[];
  prevYearSupData?: any[];
  totalCost?: string;
};

// Separate Document component without PDFViewer for downloads
export const ReportDocument: React.FC<ReportPdfProps> = ({
  reportData,
  prevYearReport,
  ecomodClasses,
  supClasses,
  pricesData,
  reportCompany,
  reportOrganization,
  consumerData,
  businessData,
  supData,
  prevYearBusinessData,
  prevYearConsumerData,
  prevYearSupData,
  totalCost,
}) => {
  const { t } = useTranslation();

  const numberToFixed = (number: number) => {
    const fixedNumber = number.toFixed(2);
    return fixedNumber.endsWith(".00") ? fixedNumber.slice(0, -3) : fixedNumber;
  };

  const currentYearReportingCycle =
    reportData?.quarter && isInteger(reportData.quarter.toString())
      ? ReportingCycleEnum.QUARTERLY
      : ReportingCycleEnum.YEARLY;

  const prevYearReportingCycle =
    prevYearReport?.quarter && isInteger(prevYearReport.quarter.toString())
      ? ReportingCycleEnum.QUARTERLY
      : ReportingCycleEnum.YEARLY;

  const getEcomodContent = (
    ecomod: {
      key: string;
      label: string;
      combinedWeight: number;
      price?: number | undefined;
    },
    prevYearEcomodWeight: number | undefined,
  ) => {
    const kgChange = prevYearEcomodWeight
      ? numberToFixed(ecomod.combinedWeight - prevYearEcomodWeight)
      : "-";
    const percentageChange = prevYearEcomodWeight
      ? getChangePercentageComparedToPrevYear(
          ecomod.combinedWeight,
          prevYearEcomodWeight,
          currentYearReportingCycle,
          prevYearReportingCycle,
        )
      : "-";

    return (
      <View
        style={{
          ...styles.sectionRow,
        }}
        key={ecomod.key}
      >
        <Text
          style={{
            ...styles.nameColumn,
            paddingLeft: 10,
          }}
        >
          {ecomod.label}
        </Text>
        <Text style={styles.numberColumn}>
          {ecomod.combinedWeight
            ? numberToFixed(ecomod.combinedWeight) + " kg"
            : ""}
        </Text>
        <Text style={styles.numberColumn}>
          {kgChange !== "-" ? kgChange + " kg" : "-"}
        </Text>
        <Text style={styles.numberColumn}>{percentageChange}</Text>
        <Text style={styles.numberColumn}>
          {numberToFixed(ecomod.price as number)} €
        </Text>
      </View>
    );
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.header}>
          {t("header-report") +
            " " +
            reportData?.year +
            (reportData?.quarter ? " Q" + reportData?.quarter : "") +
            " - " +
            (reportOrganization
              ? reportOrganization.organizationName
              : reportCompany?.companyName)}
        </Text>
        <View style={styles.section}>
          <Text style={styles.sectionHeader}>{t("label-consumer")}</Text>
          <View style={styles.sectionContent}>
            <View style={styles.headerSectionRow}>
              <Text style={styles.nameColumn}>{t("label-name")}</Text>
              <Text style={styles.numberColumn}>
                {t("header-total-amount")}
              </Text>
              <Text style={styles.numberColumn}>{t("header-change-kg")}</Text>
              <Text style={styles.numberColumn}>
                {t("header-change-percentage")}
              </Text>
              <Text style={styles.numberColumn}>
                {t("header-price") + " " + t("label-vat-amount")}
              </Text>
            </View>
            {consumerData &&
              consumerData.map((row) => {
                const prevYearWeight = prevYearConsumerData?.find(
                  (item) => item.key === row.key,
                )?.combinedWeight;

                return (
                  <View key={row.key}>
                    <View style={styles.sectionRow}>
                      <Text style={styles.nameColumn}>{row.label}</Text>
                      <Text style={styles.numberColumn}>
                        {numberToFixed(row.combinedWeight) + " kg"}
                      </Text>
                      <Text style={styles.numberColumn}>
                        {prevYearWeight
                          ? numberToFixed(row.combinedWeight - prevYearWeight) +
                            " kg"
                          : "-"}
                      </Text>
                      <Text style={styles.numberColumn}>
                        {prevYearWeight
                          ? getChangePercentageComparedToPrevYear(
                              row.combinedWeight,
                              prevYearWeight,
                              currentYearReportingCycle,
                              prevYearReportingCycle,
                            )
                          : "-"}
                      </Text>
                      <Text style={styles.numberColumn}>
                        {numberToFixed(row.price as number)} €
                      </Text>
                    </View>
                    {row.ecomodMaterials.map((ecomod) => {
                      const prevYearEcomodWeight = prevYearConsumerData
                        ?.find((item) => item.key === row.key)
                        ?.ecomodMaterials.find(
                          (mat) => mat.key === ecomod.key,
                        )?.combinedWeight;

                      if (!ecomod.combinedWeight) return null;

                      return getEcomodContent(ecomod, prevYearEcomodWeight);
                    })}
                  </View>
                );
              })}
          </View>
        </View>
        <View style={styles.section}>
          <Text style={styles.sectionHeader}>{t("label-business")}</Text>
          <View style={styles.sectionContent}>
            <View style={styles.headerSectionRow}>
              <Text style={styles.nameColumn}>{t("label-name")}</Text>
              <Text style={styles.numberColumn}>
                {t("header-total-amount")}
              </Text>
              <Text style={styles.numberColumn}>{t("header-change-kg")}</Text>
              <Text style={styles.numberColumn}>
                {t("header-change-percentage")}
              </Text>
              <Text style={styles.numberColumn}>
                {t("header-price") + " " + t("label-vat-amount")}
              </Text>
            </View>
            {businessData &&
              businessData.map((row) => {
                const prevYearWeight = prevYearBusinessData?.find(
                  (item) => item.key === row.key,
                )?.combinedWeight;

                return (
                  <View key={row.key}>
                    <View style={styles.sectionRow}>
                      <Text style={styles.nameColumn}>{row.label}</Text>
                      <Text style={styles.numberColumn}>
                        {numberToFixed(row.combinedWeight) + " kg"}
                      </Text>
                      <Text style={styles.numberColumn}>
                        {prevYearWeight
                          ? numberToFixed(row.combinedWeight - prevYearWeight) +
                            " kg"
                          : "-"}
                      </Text>
                      <Text style={styles.numberColumn}>
                        {prevYearWeight
                          ? getChangePercentageComparedToPrevYear(
                              row.combinedWeight,
                              prevYearWeight,
                              currentYearReportingCycle,
                              prevYearReportingCycle,
                            )
                          : "-"}
                      </Text>
                      <Text style={styles.numberColumn}>
                        {numberToFixed(row.price as number)} €
                      </Text>
                    </View>
                    {row.ecomodMaterials.map((ecomod) => {
                      const prevYearEcomodWeight = prevYearBusinessData
                        ?.find((item) => item.key === row.key)
                        ?.ecomodMaterials.find(
                          (mat) => mat.key === ecomod.key,
                        )?.combinedWeight;

                      if (!ecomod.combinedWeight) return null;

                      return getEcomodContent(ecomod, prevYearEcomodWeight);
                    })}
                  </View>
                );
              })}
          </View>
        </View>
        <View style={styles.section}>
          <Text style={styles.sectionHeader}>
            {t("label-single-use-plastic")}
          </Text>
          <View style={styles.sectionContent}>
            <View style={styles.headerSectionRow}>
              <Text style={styles.nameColumn}>{t("label-name")}</Text>
              <Text style={styles.numberColumn}>
                {t("header-total-amount")}
              </Text>
              <Text style={styles.numberColumn}>{t("header-change-kg")}</Text>
              <Text style={styles.numberColumn}>
                {t("header-change-percentage")}
              </Text>
              <Text style={styles.numberColumn}>
                {t("header-price") + " " + t("label-vat-amount")}
              </Text>
            </View>
            {supData &&
              supData.map((row) => {
                const prevYearWeight = prevYearSupData?.find(
                  (item) => item.key === row.key,
                )?.combinedWeight;

                return (
                  <View key={row.key}>
                    <View style={styles.sectionRow}>
                      <Text style={styles.nameColumn}>{row.label}</Text>
                      <Text style={styles.numberColumn}>
                        {numberToFixed(row.combinedWeight) + " kg"}
                      </Text>
                      <Text style={styles.numberColumn}>
                        {prevYearWeight
                          ? numberToFixed(row.combinedWeight - prevYearWeight) +
                            " kg"
                          : "-"}
                      </Text>
                      <Text style={styles.numberColumn}>
                        {prevYearWeight
                          ? getChangePercentageComparedToPrevYear(
                              row.combinedWeight,
                              prevYearWeight,
                              currentYearReportingCycle,
                              prevYearReportingCycle,
                            )
                          : "-"}
                      </Text>
                      <Text style={styles.numberColumn}>
                        {numberToFixed(row.price as number)} €
                      </Text>
                    </View>
                    {row.ecomodMaterials.map((ecomod) => {
                      const prevYearEcomodWeight = prevYearBusinessData
                        ?.find((item) => item.key === row.key)
                        ?.ecomodMaterials.find(
                          (mat) => mat.key === ecomod.key,
                        )?.combinedWeight;

                      if (!ecomod.combinedWeight) return null;

                      return getEcomodContent(ecomod, prevYearEcomodWeight);
                    })}
                  </View>
                );
              })}
          </View>
        </View>
        <View
          style={{
            marginTop: 10,
            alignItems: "flex-end",
          }}
        >
          <Text style={{ fontSize: 10, fontWeight: "bold" }}>
            {`${t("label-total-cost")}: ${totalCost ?? "0"} € ${t("label-vat-amount")}`}
          </Text>
          <Text
            style={{
              fontSize: 8,
              color: "#797979",
              fontWeight: "normal",
              marginTop: 4,
            }}
          >
            {t("label-total-amount-notification")}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

// Wrapper with PDFViewer for preview
export const ReportPdf: React.FC<ReportPdfProps> = (props) => {
  return (
    <PDFViewer style={{ width: "100%", height: "85vh" }}>
      <ReportDocument {...props} />
    </PDFViewer>
  );
};

export default ReportPdf;
